import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Clock,
  Sparkles,
  ArrowRight,
  Loader2,
  CheckCircle2,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Modal shown when demo expires
 * Offers options to extend demo or sign up for full account
 */
export const DemoExpiredModal = ({
  isOpen,
  onClose,
  userEmail,
  onRequestExtension
}) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('options'); // 'options' | 'extend' | 'success'
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState(userEmail || '');

  const handleRequestExtension = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setIsLoading(true);
    try {
      // Call extension request (could be email to sales or new demo)
      if (onRequestExtension) {
        await onRequestExtension(email);
      }
      setMode('success');
    } catch (error) {
      toast.error('Failed to request extension. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = () => {
    onClose?.();
    navigate('/signup');
  };

  const handleLogin = () => {
    onClose?.();
    navigate('/login');
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {mode === 'options' && (
          <>
            <DialogHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
              <DialogTitle className="text-2xl">Demo Expired</DialogTitle>
              <DialogDescription className="text-base mt-2">
                Your 24-hour demo period has ended. Don't worry - you can continue exploring or upgrade to a full account.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 mt-6">
              <Button
                className="w-full h-12 bg-slate-900 hover:bg-slate-800"
                onClick={handleSignUp}
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Create Full Account
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              <Button
                variant="outline"
                className="w-full h-12"
                onClick={() => setMode('extend')}
              >
                <Clock className="w-5 h-5 mr-2" />
                Request Demo Extension
              </Button>

              <div className="text-center pt-2">
                <Button
                  variant="link"
                  className="text-slate-500"
                  onClick={handleLogin}
                >
                  Already have an account? Sign in
                </Button>
              </div>
            </div>
          </>
        )}

        {mode === 'extend' && (
          <>
            <DialogHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Mail className="w-8 h-8 text-blue-600" />
              </div>
              <DialogTitle className="text-2xl">Request Extension</DialogTitle>
              <DialogDescription className="text-base mt-2">
                Enter your email and we'll send you a new demo link with another 24 hours.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-6">
              <div className="space-y-2">
                <Label htmlFor="extension-email">Email Address</Label>
                <Input
                  id="extension-email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11"
                />
              </div>

              <Button
                className="w-full h-12 bg-blue-600 hover:bg-blue-700"
                onClick={handleRequestExtension}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send New Demo Link
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>

              <Button
                variant="ghost"
                className="w-full"
                onClick={() => setMode('options')}
              >
                Back to options
              </Button>
            </div>
          </>
        )}

        {mode === 'success' && (
          <>
            <DialogHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <DialogTitle className="text-2xl">Check Your Email</DialogTitle>
              <DialogDescription className="text-base mt-2">
                We've sent a new demo link to <strong>{email}</strong>. Check your inbox to continue exploring.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 mt-6">
              <Button
                className="w-full h-12"
                onClick={handleLogin}
              >
                Go to Login
              </Button>

              <div className="text-center">
                <p className="text-sm text-slate-500">
                  Didn't receive it?{' '}
                  <Button
                    variant="link"
                    className="px-0 text-slate-900"
                    onClick={() => {
                      setMode('extend');
                      handleRequestExtension();
                    }}
                  >
                    Send again
                  </Button>
                </p>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DemoExpiredModal;
